import type { FilesApi } from '@odo/shared-ui';
import {
    deletePracticeDocument,
    fetchPracticeDocumentDownloadUrl,
    uploadPracticeDocument,
    type PracticeDocumentType
} from './practiceDocuments';

/**
 * Practice-documents implementation of the shared files module's IO seam
 * (see services/filesApi.ts for the patient-files counterpart). The backend
 * has no PATCH for practice documents and no cross-origin (Monday-hosted)
 * sources, so `updateFile` and `fetchViaProxy` are unreachable in practice -
 * capabilities in PracticeFilesSection (canRename/canReclassify: false) keep
 * the module from ever calling them; they throw rather than silently no-op
 * so a capability regression surfaces immediately instead of failing quietly.
 */
export const practiceFilesApi: FilesApi = {
    getDownloadUrl: async (practiceId, practiceFileId) => {
        const { url } = await fetchPracticeDocumentDownloadUrl(practiceId, practiceFileId);
        return url;
    },
    deleteFile: (practiceId, practiceFileId) => deletePracticeDocument(practiceId, practiceFileId),
    updateFile: () => {
        throw new Error('Practice documents cannot be renamed or reclassified.');
    },
    uploadFile: async (practiceId, file, options) => {
        try {
            const documentType = (options?.fileType as PracticeDocumentType | undefined) ?? 'other';
            const description =
                typeof options?.metadata?.description === 'string'
                    ? options.metadata.description
                    : undefined;
            const doc = await uploadPracticeDocument(
                practiceId,
                { file, documentType, description },
                options?.onProgress
            );
            return { success: true, patientFileId: doc.id, fileId: doc.fileId };
        } catch (err) {
            return { success: false, error: err instanceof Error ? err.message : 'Upload failed' };
        }
    },
    fetchViaProxy: () => {
        throw new Error('Practice documents have no proxy-fetch sources.');
    }
};
